import { Typography } from "@hellocarbo/lasagne";
import { use } from "react";
import { useTranslation } from "react-i18next";
import { LoaderFunctionArgs, useLoaderData } from "react-router";
import invariant from "tiny-invariant";

import { NavigationMenuLinks } from "@/components/NavigationMenu";
import ExportCard from "@/components/ReviewExports/ExportCard";
import { UserContext } from "@/contexts";
import { TExportUrls } from "@/types";

import { useReviewContext } from "./Review";

export const handle = {
  activeMenuLink: NavigationMenuLinks.restitution,
  breadcrumb: ({ reviewId }: { reviewId: string }) => {
    return [
      {
        href: `/reviews/${reviewId}/exports`,
        label: "review:exports.title",
      },
    ];
  },
};

export async function loader({ params }: LoaderFunctionArgs) {
  const { reviewId } = params;

  invariant(reviewId, "No reviewId found");

  const exportUrlsResponse = await fetch(
    `/api/client/reviews/${reviewId}/exports`
  );

  if (exportUrlsResponse.status !== 200) {
    throw new Error("Failed to load the review data");
  }

  const { data: exportUrls } = (await exportUrlsResponse.json()) as {
    data: TExportUrls;
  };

  return exportUrls;
}

function ReviewExports() {
  const { reviewData } = useReviewContext();
  const {
    emission_factors_url,
    scopes_beges_url,
    scopes_ghg_url,
    product_materials_url,
  } = useLoaderData<typeof loader>();
  const { t } = useTranslation(["formatter", "review"]);
  const { user } = use(UserContext);

  const { period_start, period_end } = reviewData;

  const isFreeTrial = user?.company.subscriptionType === "start";

  return (
    <div>
      <Typography step="3" as="h1" weight="bold" className="mb-3xs">
        {t("review:exports.title")}
      </Typography>
      <Typography className="text-grayscale-dark">
        {t("formatter:date", { value: new Date(period_start) })}
        &nbsp;-&nbsp;
        {t("formatter:date", { value: new Date(period_end) })}
      </Typography>
      {
        <div className="my-md gap-md grid grid-cols-2">
          {(emission_factors_url || isFreeTrial) && (
            <ExportCard
              title={t("review:exports.emissionFactors.title")}
              description={t("review:exports.emissionFactors.description")}
              exports={[
                {
                  name: t("review:exports.emissionFactors.exportName"),
                  url: emission_factors_url,
                  segmentEventName: "emission_factors_export_downloaded",
                },
              ]}
            />
          )}
          {(scopes_beges_url || scopes_ghg_url || isFreeTrial) && (
            <ExportCard
              title={t("review:exports.scopes.title")}
              description={t("review:exports.scopes.description")}
              exports={[
                ...(scopes_ghg_url || isFreeTrial
                  ? [
                      {
                        name: t("review:exports.scopes.ghgExportName"),
                        url: scopes_ghg_url,
                        segmentEventName: "ghg_export_downloaded",
                      },
                    ]
                  : []),
                ...(scopes_beges_url || isFreeTrial
                  ? [
                      {
                        name: t("review:exports.scopes.begesExportName"),
                        url: scopes_beges_url,
                        segmentEventName: "beges_export_downloaded",
                      },
                    ]
                  : []),
              ]}
            />
          )}
          {(product_materials_url || isFreeTrial) && (
            <ExportCard
              title={t("review:exports.productMaterials.title")}
              description={t("review:exports.productMaterials.description")}
              exports={[
                {
                  name: t("review:exports.productMaterials.exportName"),
                  url: product_materials_url,
                  segmentEventName: "exhaustive_restitution_product_downloaded",
                },
              ]}
            />
          )}
        </div>
      }
    </div>
  );
}

export default ReviewExports;
